import { ExternalLink, Info, MessageSquare, Play } from "lucide-react";
import {
  getYouTubeEmbedUrl,
  isValidUrl,
  isYouTubeUrl,
  normalizeUrl,
} from "../../utils/video";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

interface ExerciseInfo {
  exerciseId: string;
  name: string;
  muscleGroup?: string;
  videoUrl?: string | null;
  descriptionUrl?: string | null;
  instructions?: string | null;
  notes?: string | null;
}

interface RecentLog {
  date: Date;
  weight: number;
  reps: number;
  rpe?: number | null;
}

interface ExerciseDetailDialogProps {
  exercise: ExerciseInfo | null;
  open: boolean;
  onClose: () => void;
  recentLogs?: RecentLog[];
  maxWeight?: number | null;
}

export function ExerciseDetailDialog({
  exercise,
  open,
  onClose,
  recentLogs = [],
  maxWeight,
}: ExerciseDetailDialogProps) {
  if (!exercise) return null;

  const normalizedVideoUrl = exercise.videoUrl
    ? normalizeUrl(exercise.videoUrl)
    : null;
  const hasVideo = normalizedVideoUrl && isValidUrl(normalizedVideoUrl);
  const normalizedDescUrl = exercise.descriptionUrl
    ? normalizeUrl(exercise.descriptionUrl)
    : null;
  const hasDescription = normalizedDescUrl && isValidUrl(normalizedDescUrl);

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[95vh] w-[95vw] max-w-xl overflow-hidden rounded-xl border bg-background p-0 sm:w-full">
        <DialogHeader className="border-b px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <DialogTitle className="truncate font-bold text-foreground text-lg sm:text-xl">
                {exercise.name}
              </DialogTitle>
              {exercise.muscleGroup && (
                <div className="mt-1">
                  <span className="rounded-full bg-muted px-2.5 py-0.5 font-semibold text-[10px] text-muted-foreground uppercase tracking-wider">
                    {exercise.muscleGroup}
                  </span>
                </div>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="no-scrollbar max-h-[calc(95vh-80px)] overflow-y-auto px-4 py-6 sm:px-6">
          <div className="space-y-8">
            {/* Video Section */}
            {hasVideo && normalizedVideoUrl && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Play className="h-3.5 w-3.5 text-muted-foreground" />
                  <h4 className="font-semibold text-[10px] text-muted-foreground uppercase tracking-wide">
                    Uygulama Videosu
                  </h4>
                </div>
                <div className="relative aspect-video overflow-hidden rounded-xl border bg-black">
                  {isYouTubeUrl(normalizedVideoUrl) ? (
                    <iframe
                      src={getYouTubeEmbedUrl(normalizedVideoUrl)}
                      title={`${exercise.name} video`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="h-full w-full border-0"
                    />
                  ) : (
                    <a
                      href={normalizedVideoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-full w-full items-center justify-center bg-muted/10 transition-colors hover:bg-muted/20"
                    >
                      <div className="flex flex-col items-center gap-3 text-muted-foreground">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/5">
                          <Play className="h-5 w-5 fill-current text-primary" />
                        </div>
                        <span className="font-semibold text-xs">
                          Videoyu İzle
                        </span>
                      </div>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Coach Notes */}
            {exercise.notes && (
              <div className="relative space-y-3 overflow-hidden rounded-xl border bg-muted/20 p-4">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                  <h4 className="font-semibold text-[10px] text-muted-foreground uppercase tracking-wide">
                    Antrenör Notu
                  </h4>
                </div>
                <p className="whitespace-pre-wrap text-foreground text-sm leading-relaxed">
                  {exercise.notes}
                </p>
              </div>
            )}

            {/* Instructions */}
            {exercise.instructions && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                  <h4 className="font-semibold text-[10px] text-muted-foreground uppercase tracking-wide">
                    Hareket Talimatları
                  </h4>
                </div>
                <div className="rounded-xl border border-border/50 bg-muted/5 p-4">
                  <p className="whitespace-pre-wrap text-muted-foreground text-sm leading-relaxed">
                    {exercise.instructions}
                  </p>
                </div>
              </div>
            )}

            {/* Stats Row */}
            {(maxWeight != null || hasDescription) && (
              <div className="grid grid-cols-2 gap-4">
                {maxWeight != null && (
                  <div className="flex flex-col gap-1 rounded-xl border bg-card p-4">
                    <span className="font-semibold text-[9px] text-muted-foreground/60 uppercase tracking-wider">
                      1RM (Tahmini)
                    </span>
                    <div className="flex items-baseline gap-1">
                      <span className="font-bold text-foreground text-xl">
                        {maxWeight}
                      </span>
                      <span className="font-semibold text-[10px] text-muted-foreground/40">
                        kg
                      </span>
                    </div>
                  </div>
                )}
                {hasDescription && normalizedDescUrl && (
                  <div className="rounded-xl border bg-card p-4 transition-colors hover:bg-muted/50">
                    <span className="mb-2 block font-semibold text-[9px] text-muted-foreground/60 uppercase tracking-wider">
                      Kaynaklar
                    </span>
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0 font-semibold text-primary text-xs underline-offset-4"
                      asChild
                    >
                      <a
                        href={normalizedDescUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5"
                      >
                        <ExternalLink className="h-3 w-3" />
                        İncele
                      </a>
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Performance History */}
            {recentLogs.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-[10px] text-muted-foreground uppercase tracking-wide">
                    Son Performans
                  </h4>
                  <div className="ml-4 h-px flex-1 bg-border/40" />
                </div>
                <div className="space-y-2">
                  {recentLogs.slice(0, 5).map((log, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between rounded-lg border bg-muted/5 px-4 py-3 transition-colors hover:bg-muted/10"
                    >
                      <div className="flex flex-col">
                        <span className="font-semibold text-[9px] text-muted-foreground/50 uppercase">
                          {formatDate(log.date)}
                        </span>
                        <div className="flex items-baseline gap-1">
                          <span className="font-bold text-foreground text-sm uppercase">
                            {log.weight}kg
                          </span>
                          <span className="font-semibold text-[10px] text-muted-foreground/40">
                            × {log.reps}
                          </span>
                        </div>
                      </div>
                      {log.rpe && (
                        <div className="rounded bg-muted px-2 py-0.5">
                          <span className="font-bold text-[10px] text-muted-foreground">
                            @RPE{log.rpe}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {!hasVideo &&
              !exercise.instructions &&
              !exercise.notes &&
              !hasDescription &&
              recentLogs.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-center opacity-40">
                  <Info className="mb-3 h-10 w-10 text-muted-foreground" />
                  <p className="font-medium text-sm">
                    Bu hareket için henüz detay eklenmemiş.
                  </p>
                </div>
              )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function formatDate(date: Date): string {
  return new Intl.DateTimeFormat("tr-TR", {
    day: "numeric",
    month: "short",
  }).format(date);
}
